import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { ProgressService } from '@layout/service/progress.service';
import { ToastService } from '@layout/service/toast.service';

@Injectable()
export class ReportingInterceptor implements HttpInterceptor {
  private progress = inject(ProgressService);
  private toast = inject(ToastService);

  private validateError(obj: unknown, key: string): boolean {
    return (
      obj != null &&
      typeof obj === 'object' &&
      key in obj &&
      (obj as Record<string, unknown>)[key] !== undefined &&
      (obj as Record<string, unknown>)[key] !== null &&
      (obj as Record<string, unknown>)[key] !== ''
    );
  }

  // Flatten field errors, e.g. { non_field_errors: ['msg'], name: 'msg' } -> ['msg', 'msg']
  private extractErrorMessages(errors: unknown): string[] {
    if (typeof errors === 'string') return errors ? [errors] : [];
    if (Array.isArray(errors)) return errors.flatMap((e) => this.extractErrorMessages(e));
    if (errors != null && typeof errors === 'object') {
      return Object.values(errors).flatMap((e) => this.extractErrorMessages(e));
    }
    return [];
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    this.progress.beginProgress();
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error instanceof HttpErrorResponse) {
          let message = error.statusText || 'Unknown error.';
          let summary = 'Error';

          // Handle new API error format
          if (this.validateError(error, 'error')) {
            const errorBody = error.error;

            // Check for new API format with message and errors
            if (this.validateError(errorBody, 'message')) {
              summary = errorBody.message; // Use message as title (e.g., "Validation failed.")

              // Check if there are detailed errors
              if (this.validateError(errorBody, 'errors') && typeof errorBody.errors === 'object') {
                // Extract all error messages from the errors object
                const errorMessages = this.extractErrorMessages(errorBody.errors);
                if (errorMessages.length > 0) {
                  message = errorMessages.join('\n');
                }
              } else if (typeof errorBody.message === 'string') {
                // If no detailed errors, use the message as both title and body
                message = errorBody.message;
              }
            }
            // Check for old API format with detail
            else if (this.validateError(errorBody, 'detail')) {
              if (typeof errorBody.detail === 'string') {
                message = errorBody.detail;
              } else if (typeof errorBody.detail === 'object' && errorBody.detail !== null) {
                const detail = errorBody.detail as Record<string, unknown>;
                if (typeof detail['message'] === 'string') {
                  summary = detail['message'];
                }
                if (detail['errors'] != null && typeof detail['errors'] === 'object') {
                  const errorMessages = this.extractErrorMessages(detail['errors']);
                  if (errorMessages.length > 0) {
                    message = errorMessages.join('\n');
                  } else if (typeof detail['message'] === 'string') {
                    message = detail['message'];
                  }
                } else if (typeof detail['message'] === 'string') {
                  message = detail['message'];
                }
              }
            }
          }

          // Determine summary based on status code (only if not already set from API response)
          if (summary === 'Error') {
            if (error.status === 403 || error.status === 401) {
              summary = 'Permission Error';
            } else if (error.status === 404) {
              summary = 'Not Found';
            } else if (error.status >= 500) {
              summary = 'Server Error';
            }
          }

          // Show error immediately, don't wait for isFinalError flag
          this.toast.showError(summary, message);
        }
        return throwError(() => error);
      }),
      finalize(() => {
        this.progress.endProgress();
      })
    );
  }
}

export const reportingInterceptor = {
  provide: HTTP_INTERCEPTORS,
  useClass: ReportingInterceptor,
  deps: [ProgressService, ToastService],
  multi: true,
};
